import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  VueFormError,
  VueNavigationService,
  VueTranslationService,
  WINDOW
} from '@vue/vueframework';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AppConfigService } from '../shared/services/app-config.service';
import { TestCenterService } from '../shared/services/test-center.service';

@Component({
  selector: 'app-vue-id-validation',
  templateUrl: './vue-id-validation.component.html',
  styleUrls: [ './vue-id-validation.component.scss' ],
  standalone: false
})
export class VueIdValidationComponent implements OnInit, OnDestroy {
  @ViewChild('validateForm') public vueIdForm: NgForm;

  public navSub: Subscription;

  public vueId: number;
  public vueIdValidated: boolean;
  public formError = new VueFormError();

  public showForm = false;

  /**
   * i18n keys used by this component
   */
  public translationKeys = [ 'error.InvalidVueID' ];

  /**
   * Map used to hold the translations
   */
  public translations: Record<string, string>;

  public constructor(
    private router: Router,
    private route: ActivatedRoute,
    private testCenterServices: TestCenterService,
    private vueTranslationService: VueTranslationService,
    private vueNavService: VueNavigationService,
    private ngbModal: NgbModal,
    private appConfigService: AppConfigService,
    @Inject(WINDOW) private vueWindow: Window
  ) {}

  /**
   * Upon loading this element, we will add the 'hide-menu' class
   * to root that will hide certain menu elements (css found in styles.scss)
   * It will also make sure the main nav is marked closed so
   * we don't have the the left hand side padding.
   */
  public ngOnInit(): void {
    this.showForm = false;
    this.translations = this.vueTranslationService.getTranslations(
      'Connect',
      this.translationKeys
    );
    this.vueWindow.document.querySelector('html').classList.add('hide-menu');
    this.vueNavService.changeCurrentMenuStateClosed();

    /**
     * Certain async actions after login could finish after this loads
     * and attempt to open the menu. We keep it closed
     */
    this.navSub = this.vueNavService.getMenuState().subscribe((val) => {
      if (val === 'menu-open') {
        this.vueNavService.changeCurrentMenuStateClosed();
      }
    });

    if (this.route.snapshot.data.showVueId) {
      this.showForm = true;
    } else if (this.ngbModal.hasOpenModals()) {
      /**
       * If we have any open modals (ex: change locale confirmation modal)
       * we will subscribe and wait till the user closes
       * the modal
       */
      const sub = this.ngbModal.activeInstances
        .pipe(filter((i) => !i.length))
        .subscribe(() => {
          sub.unsubscribe();
          this.next();
        });
    } else {
      this.next();
    }
  }

  /**
   * Is called when we navigate away from this component.
   * It will remove our 'hide-menu' that hides navigation
   * components and open the vue navigation menu. This is here
   * instead of next() because they might dump out of this component
   * with a logout.
   */
  public ngOnDestroy(): void {
    this.navSub.unsubscribe();
    this.vueWindow.document.querySelector('html').classList.remove('hide-menu');
  }

  /**
   * Called when we are done and ready to move on, will also
   * call ngOnDestroy which will remove our hide-menu class
   */
  public next(): void {
    this.navSub.unsubscribe();

    if (environment.production) {
      this.vueNavService.selectApplication(192);
    } else {
      this.vueNavService.changeCurrentMenuStateOpen();
      this.router.navigateByUrl('/pleaseSelect');
    }
  }

  public onVueIdChange(): void {
    this.vueIdValidated = false;
  }

  public validateVueId(): void {
    if (this.vueIdForm.valid) {
      this.testCenterServices.validateVueId(this.vueId).subscribe({
        error: () => {
          this.vueIdValidated = false;
        },
        next: (valid: boolean) => {
          if (valid) {
            this.vueIdValidated = true;
            return;
          }
          this.formError.invalidFormSubmit = true;
          this.formError.fieldInError = 'vueID';
          this.vueIdForm.form.controls.vueID.setErrors({
            vueCustomValidation: this.translations[ 'error.InvalidVueID' ]
          });
        }
      });
    } else {
      this.formError.invalidFormSubmit = true;
      this.formError.fieldInError = 'vueID';
    }
  }

  public submit(): void {
    this.testCenterServices.createVueId(this.vueId).subscribe({
      next: () => {
        this.next();
      }
    });
  }
}
